import { type SubScores } from "./scoring";

/** Kandidaten-Status lifecycle (MVP_requirements.md §5). English identifiers
 * match the candidate lifecycle state machine; the backend may store German
 * values. */
export const CANDIDATE_STATUSES = [
  "pool",
  "longlist",
  "selected",
  "outreach",
  "prequal",
  "interview",
  "recommendation",
  "placed",
  "rejected",
  // Display-only: how the API shows a recruiter a candidate in a recommendation
  // that is not released yet (ADR 0004). Never sent back as a status change.
  "in_review",
] as const;
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];

/** Manual override of the auto-match for a single stelle skill.
 * Not-set keys fall back to case-insensitive substring matching. */
export interface SkillMatch {
  mustHaves: Record<string, boolean>;
  shouldHaves: Record<string, boolean>;
}

export interface CareerEntry {
  /** 0-based order within the timeline; unique per candidate. */
  position: number;
  /** Free-form start, e.g. "2019-01" or "2019". */
  fromDate: string;
  /** Free-form end. "present" is a valid value. */
  toDate: string;
  title: string;
  company: string;
  description: string | null;
}

/** One education row on a candidate's CV. Populated by the Xing SSR
 * cache parser and the LinkedIn-enrichment Claude prompt. */
export interface EducationEntry {
  school: string;
  degree: string;
  fieldOfStudy: string;
  /** Free-form, e.g. "2014" or "2014-09". */
  startDate: string;
  endDate: string;
  description: string | null;
}

export const LANGUAGE_PROFICIENCIES = [
  "native",
  "fluent",
  "professional",
  "conversational",
  "basic",
] as const;
export type LanguageProficiency = (typeof LANGUAGE_PROFICIENCIES)[number];

/** One language a candidate speaks, with proficiency where available. */
export interface CandidateLanguage {
  name: string;
  proficiency: LanguageProficiency | "";
}

export const SKILL_CATEGORIES = [
  "fachlich",
  "programming",
  "framework",
  "tool",
  "methodology",
  "language",
  "soft",
  "certification",
  "other",
] as const;
export type SkillCategory = (typeof SKILL_CATEGORIES)[number];

export const SKILL_CATEGORY_LABELS: Record<SkillCategory, string> = {
  fachlich: "Fachlich",
  programming: "Programmiersprache",
  framework: "Framework / Library",
  tool: "Tool / Software",
  methodology: "Methodik",
  language: "Sprache",
  soft: "Soft Skill",
  certification: "Zertifikat",
  other: "Sonstiges",
};

/** A skill attributed to the candidate via the Skill master table. */
export interface CandidateSkillEntry {
  name: string;
  slug: string;
  category: SkillCategory;
  /** Provenance — "linkedin", "xing", "manual" today. */
  source: string;
}

export type CandidateSource = "LinkedIn" | "Xing" | "Direct approach" | "Cold inbound" | "Other";

/** Where the candidate is willing to work. Empty string = unknown,
 * carried over from old data where the field wasn't captured. */
export const WORK_LOCATION_PREFERENCES = ["remote", "hybrid", "onsite", "flexible"] as const;
export type WorkLocationPreference = (typeof WORK_LOCATION_PREFERENCES)[number];

export const WORK_LOCATION_LABELS: Record<WorkLocationPreference, string> = {
  remote: "Remote",
  hybrid: "Hybrid",
  onsite: "Vor-Ort",
  flexible: "Flexibel",
};

/** Wechselbereitschaft — how actively the candidate is open to a new role. */
export const CHANGE_READINESS = ["aktiv", "offen", "passiv"] as const;
export type ChangeReadiness = (typeof CHANGE_READINESS)[number];

export const CHANGE_READINESS_LABELS: Record<ChangeReadiness, string> = {
  aktiv: "Aktiv suchend",
  offen: "Offen für Angebote",
  passiv: "Nicht aktiv",
};

/** The Anonym-Ansicht's view of a candidate (#140), sent to admins only.
 * Companies in texts are [company classes], schools [Hochschule] (#172). */
export interface AnonymizedCandidate {
  /** The Candidate code, e.g. „K-0412". */
  code: string;
  currentCompany: string | null;
  currentTitle: string | null;
  career: { company: string; title: string | null; description: string | null }[];
  strengths: readonly string[];
  weaknesses: readonly string[];
  subReasoning: Record<string, readonly string[]>;
}

export interface Candidate {
  id: string;
  jobId: string;
  /** Admin-only: the Candidate code („K-0412"), and the Anonym-Ansicht's view (#140). */
  code?: string;
  anonymized?: AnonymizedCandidate | null;

  name: string;
  currentTitle: string | null;
  currentCompany: string | null;
  location: string | null;

  linkedinUrl: string | null;
  xingUrl: string | null;
  email: string | null;
  phone: string | null;

  /** Flat list of all skills on the candidate. */
  skills: readonly string[];
  /** Per-stelle-skill manual overrides. Keys missing from the matrix fall
   * through to the auto-match. */
  skillMatch: SkillMatch;

  cvUrl: string | null;
  photoUrl: string | null;
  careerTimeline: readonly CareerEntry[];
  /** Structured education entries (Finding 5 / 8). May be empty for
   * candidates whose source profile didn't surface education. */
  educationEntries: readonly EducationEntry[];
  /** Languages with proficiency, from LinkedIn enrichment. */
  languages: readonly CandidateLanguage[];
  /** Skills normalised through the Skill master table. The flat `skills`
   * array above is the legacy view; `candidateSkills` is canonical for
   * any new UI that wants category / source info. */
  candidateSkills: readonly CandidateSkillEntry[];

  status: CandidateStatus;
  /** Position in the Empfehlung ranking, 1-based. `null` when not promoted. */
  recommendationRank: number | null;
  /** Overall score 0–100. `null` before scoring. */
  score: number | null;
  /** `title` only on a candidate the Vorauswahl sorted out (#135). */
  subScores: SubScores & { title?: number };
  /** Per-axis Claude reasoning bullets. Keys mirror `subScores`. Empty
   * when the algorithmic fallback scored the candidate. */
  subReasoning: Record<string, readonly string[]>;

  strengths: readonly string[];
  weaknesses: readonly string[];

  /** Distance from the stelle's company city, in km. Derived at ingest. */
  distanceKm: number | null;

  /** Visible to recruiter + admin. */
  recruiterNotes: string | null;
  /** Admin-only (MVP_requirements.md §4.6a / §4.7). */
  internalNotes: string | null;
  interviewNotes: string | null;
  /** Visible to recruiter on the Empfehlung page once promoted. */
  interviewKeyFindings: readonly string[];
  /** Recommendation blurb written by the admin, shown on Screen 5. */
  recommendationText: string | null;
  /** Required when `status === "rejected"`. */
  rejectionReason: string | null;

  availableFrom: string | null;
  /** Legacy free-text salary expectation kept for back-compat. New
   * filter UI reads the numeric min/max below; the chatbot or admin
   * promotes a parsed value into them. */
  salaryExpectation: string | null;
  salaryExpectationMin: number | null;
  salaryExpectationMax: number | null;
  workLocationPreference: WorkLocationPreference | "";
  changeReadiness: ChangeReadiness | "";
  source: CandidateSource | null;

  createdAt: string;
  updatedAt: string;
}
